<template>
  <div>
    <div class="text-right">
      <b-button size="sm" class="btn btn-primary add-button mb-1" variant="primary" @click="()=>openNewModal()">
        <i class="fa-solid fa-plus"></i>
        {{ $t('Add CPA') }}
      </b-button>
    </div>
    <b-table
        v-if="affiliate.affiliateCpas!=null"
        ref="cpaTable"
        striped
        hover
        responsive
        v-handy-scroll
        v-bind:items="affiliate.affiliateCpas"
        v-bind:fields="fields"
        @row-clicked="openNewModal"
    >
      <template #cell(creationTime)="data">
        {{
          $moment(data.value)
              .format('DD/MM/YYYY HH:mm:ss')
        }}
      </template>

      <template #cell(cpa)="data">
        {{ convertToEURO(data.value) }}
      </template>

      <template #cell(type)="data">
        <b-badge :variant="data.value === 'FLAT' ? 'light-success' : 'light-primary'">
          {{ data.value === 'FLAT' ? $t('affiliates.cpaTypeFlat') : $t('affiliates.cpaTypeCrg') }}
        </b-badge>
      </template>

      <template #cell(crg)="data">
        <span v-if="data.item.type !== 'FLAT' && data.value != null">{{ data.value }}%</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(startDate)="data">
        {{ data.value ? $moment(data.value).format('DD/MM/YYYY') : '—' }}
      </template>

      <template #cell(endDate)="data">
        <b-badge v-if="isActiveCpa(data.item)" variant="light-success">
          <i class="fa-solid fa-bolt mr-25"></i>{{ $t('operations.active') }}
        </b-badge>
        <span v-else>{{ data.value ? $moment(data.value).format('DD/MM/YYYY') : '—' }}</span>
      </template>

      <template #cell(actions)="row">
        <b-button size="sm" variant="outline-danger" v-b-tooltip.hover :title="$t('operations.delete')"
                  @click.stop="deleteOpen(row.item)"
        >
          <i class="fa-solid fa-trash-can"></i>
        </b-button>
      </template>

    </b-table>

    <!-- Delete CPA confirmation -->
    <b-modal v-model="showDeleteModal" centered hide-header hide-footer
             no-close-on-backdrop body-class="p-0" content-class="cpa-del-modal">
      <div class="cpa-del">
        <div class="cpa-del__icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
        <h4 class="cpa-del__title">{{ $t('affiliateCpaDelete.title') }}</h4>
        <p class="cpa-del__text">{{ $t('affiliateCpaDelete.message') }}</p>

        <div v-if="cpaToDelete" class="cpa-del__details">
          <div class="cpa-del__row">
            <span>{{ $t('columns.cpa') }} (CPA)</span><strong>{{ convertToEURO(cpaToDelete.cpa) }}</strong>
          </div>
          <div class="cpa-del__row">
            <span>{{ $t('columns.crg') }} (CRG)</span><strong>{{ cpaToDelete.crg }}</strong>
          </div>
          <div class="cpa-del__row">
            <span>{{ $t('columns.creationTime') }}</span>
            <strong>{{ $moment(cpaToDelete.creationTime).format('DD/MM/YYYY HH:mm') }}</strong>
          </div>
        </div>

        <p class="cpa-del__warn"><i class="fa-solid fa-circle-info mr-1"></i>{{ $t('affiliateCpaDelete.warning') }}</p>

        <div class="cpa-del__actions">
          <b-button variant="outline-secondary" :disabled="deleting" @click="showDeleteModal = false">
            {{ $t('operations.cancel') }}
          </b-button>
          <b-button variant="danger" :disabled="deleting" @click="confirmDelete">
            <b-spinner v-if="deleting" small class="mr-1"/>
            <i v-else class="fa-solid fa-trash-can mr-1"></i>{{ $t('operations.delete') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <affiliate-c-p-a-modal ref="cpaModal" :affiliate-id="affiliateId"
                           :affiliate-name="affiliate.company || affiliate.alias || ''" @saved="onCpaSaved"/>
  </div>
</template>

<script>
import Affiliate from '@/model/affiliate/Affiliate'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import { mapActions, mapGetters } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox'
import BusinessUnitSelectBox from '@/components/widget/select/BusinessUnitSelectBox'
import CountriesSelectBox from '@/components/widget/select/CountriesSelectBox'
import CalendarPicker from '@/components/widget/picker/calendarPicker'
import mixinBase from '@/components/mixin/mixinBase'
import AffiliateCPAModal from '@/components/modal/AffiliateCPAModal.vue'


export default {
  name: 'AffiliateEditCpa',
  components: {
    AffiliateCPAModal,
    CalendarPicker,
    CountriesSelectBox,
    BusinessUnitSelectBox,
    OrganizationSelectBox
  },
  mixins: [mixinPermissions, mixinNotifications, mixinBase],
  data: () => ({
    affiliate: Affiliate(),
    affiliateCpa: {},

    fields: [],
    showDeleteModal: false,
    cpaToDelete: null,
    deleting: false,
  }),
  props: {
    value: {
      required: true,
      default: () => ({})
    },
    newAffiliate: {
      type: Boolean,
      default: () => false
    }
  },
  watch: {
    value: {
      handler() {
        this.affiliate = Object.assign(Affiliate(), this.value)
      },
      immediate: true
    }
  },
  created() {
    this.setFields()
  },
  computed: {
    ...mapGetters('affiliate', ['']),
    affiliateId(){
      return this.affiliate?.id
    }
  },
  methods: {
    ...mapActions('affiliate', ['updateAffiliate', 'deleteCpa', 'createCpaAffiliate', 'getAffiliateById']),

    setFields() {
      let $this = this
      this.fields = [
        {
          key: 'creationTime',
          label: $this.$t('columns.creationTime'),
          sortable: true
        },
        {
          key: 'type',
          label: $this.$t('columns.type'),
          sortable: true
        },
        {
          key: 'cpa',
          label: $this.$t('columns.cpa'),
          sortable: true
        },
        {
          key: 'crg',
          label: $this.$t('columns.crg'),
          sortable: true
        },
        {
          key: 'startDate',
          label: $this.$t('affiliates.startDate'),
          sortable: true
        },
        {
          key: 'endDate',
          label: $this.$t('affiliates.endDate'),
          sortable: true
        },
        {
          key: 'actions',
          label: $this.$t('Actions'),
          sortable: false
        },
      ]
    },
    isActiveCpa(item) {
      if (item == null) return false
      return item.endDate == null || !this.$moment(item.endDate).isBefore(this.$moment(), 'day')
    },
    // Re-fetch the affiliate so the CPA table re-renders with fresh data
    // (robust regardless of what the add/edit/delete-cpa endpoints return).
    refreshAffiliate() {
      const $this = this
      if (this.affiliate == null || this.affiliate.id == null) {
        return Promise.resolve()
      }
      return this.getAffiliateById(this.affiliate.id)
          .then(fresh => {
            if (fresh != null) {
              $this.affiliate = Object.assign(Affiliate(), fresh)
              $this.$emit('input', $this.affiliate)
            }
          })
          .catch(() => {})
    },
    deleteOpen(item) {
      this.cpaToDelete = item
      this.showDeleteModal = true
    },
    async confirmDelete() {
      if (this.cpaToDelete == null) {
        return
      }
      const $this = this
      this.deleting = true
      try {
        await this.deleteCpa({ id: this.affiliate.id, cpaId: this.cpaToDelete.id })
        await this.refreshAffiliate()
        this.showSuccessNotification($this.$t('globalMessages.deleteDataSuccess', { system: $this.$t('menu.affiliates') }))
        this.showDeleteModal = false
        this.cpaToDelete = null
      } catch (err) {
        this.showErrorNotification(
            $this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.affiliates') }),
            $this.getErrorDescription(err)
        )
      } finally {
        this.deleting = false
      }
    },
    openNewModal(item) {
      this.$refs.cpaModal.show(item)
    },
    onCpaSaved() {
      return this.refreshAffiliate()
    },
  }
}
</script>

<style scoped>
.table-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1rem;
}

.add-button {
  margin-left: 1rem;
}

/* ===== Delete CPA modal ===== */
.cpa-del {
  padding: 1.75rem 1.5rem 1.5rem;
  text-align: center;
}
.cpa-del__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: #ea5455;
  background: rgba(234, 84, 85, 0.12);
}
.cpa-del__title {
  font-weight: 700;
  margin-bottom: 0.35rem;
}
.cpa-del__text {
  color: #6e6b7b;
  margin-bottom: 1rem;
}
.cpa-del__details {
  text-align: left;
  border: 1px solid rgba(34, 41, 47, 0.10);
  border-radius: 10px;
  padding: 0.5rem 0.85rem;
  margin-bottom: 1rem;
  background: rgba(34, 41, 47, 0.02);
}
.cpa-del__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0;
  font-size: 0.85rem;
}
.cpa-del__row + .cpa-del__row {
  border-top: 1px solid rgba(34, 41, 47, 0.06);
}
.cpa-del__row span { color: #6e6b7b; }
.cpa-del__warn {
  font-size: 0.8rem;
  color: #ea5455;
  margin-bottom: 1.25rem;
}
.cpa-del__actions {
  display: flex;
  gap: 0.75rem;
}
.cpa-del__actions .btn { flex: 1; }

/* Dark mode legibility */
.dark-layout .cpa-del__text,
.dark-layout .cpa-del__row span { color: #b4b7bd; }
.dark-layout .cpa-del__details {
  border-color: rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.03);
}
.dark-layout .cpa-del__row + .cpa-del__row { border-color: rgba(255, 255, 255, 0.07); }
</style>
